import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ChevronUp, GitMerge, GripVertical, Type, Unlink, X } from 'lucide-react';
import { mappingTargetState } from './schemaMappingState';
import './InboundSchemaFieldMapper.css';

export interface SchemaMappingSource {
  id: string;
  label: ReactNode;
  details?: ReactNode;
  mappedTargets?: string[];
  searchText?: string;
}

export function SchemaMappingPanels({ children, className = '', ariaLabel }: { children: ReactNode; className?: string; ariaLabel?: string }) {
  return <div role={ariaLabel ? 'group' : undefined} aria-label={ariaLabel}
    className={['inbound-field-mapper-tables', 'schema-mapping-wizard-panels', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function SchemaMappingSourceButton({ source, dragType, selected, disabled = false, onSelect, onDragStart, onDragEnd }: {
  source: SchemaMappingSource;
  dragType: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: (source: string) => void;
  onDragStart?: (source: string) => void;
  onDragEnd?: () => void;
}) {
  const targets = source.mappedTargets ?? [];
  return <button type="button" className={targets.length ? 'inbound-field-mapper-source mapped' : 'inbound-field-mapper-source'}
    aria-label={`Select ${source.id}`} aria-pressed={selected} disabled={disabled} draggable={!disabled}
    title={`Select or drag ${source.id}`} onClick={() => onSelect(source.id)}
    onDragStart={event => {
      if (disabled) { event.preventDefault(); return; }
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData(dragType, source.id);
      event.dataTransfer.setData('text/plain', source.id);
      onSelect(source.id);
      onDragStart?.(source.id);
    }} onDragEnd={onDragEnd}>
    <span className="inbound-field-mapper-source-grip"><GripVertical size={16} aria-hidden="true" /></span>
    <span className="inbound-field-mapper-source-content"><strong>{source.label}</strong>
      {source.details !== undefined && <small>{source.details}</small>}
      {targets.length > 0 && <small className="inbound-field-mapper-usage"><Check size={16} aria-hidden="true" />Mapped to {targets.length} {targets.length === 1 ? 'target' : 'targets'}: {targets.join(', ')}</small>}
    </span>
  </button>;
}

export interface SchemaMappingBlockTarget {
  id: string;
  label: ReactNode;
  details?: ReactNode;
  note?: ReactNode;
  state?: 'mapped' | 'unmapped' | 'required-unmapped';
  className?: string;
  data?: unknown;
  selectable?: boolean;
  enabled?: boolean;
  requiredToggle?: boolean;
  requiredLabel?: string;
  required?: boolean;
  requiredDisabled?: boolean;
}

export function SchemaMappingBlock({ ariaLabel, sources, targets, dragType, selectedSource, disabled = false,
  sourceHeading, sourceTableLabel, sourceSearchLabel, sourceSearchContainer, sourceNoMatch, targetHeading, targetTableLabel, mappedHeading, actionsHeading, actionsClassName, sourceEmpty, targetEmpty,
  sourceScrollClassName = '', targetScrollClassName = '', targetTableClassName = '', targetColGroup,
  onSelectSource, onSetTargetEnabled, onSetTargetRequired, onDragStart, onDragEnd, canDrop, onDropSource, renderTarget, renderActions, renderTargetHeading,
}: {
  ariaLabel: string;
  sources: SchemaMappingSource[];
  targets: SchemaMappingBlockTarget[];
  dragType: string;
  selectedSource: string | null;
  disabled?: boolean;
  sourceHeading: string;
  sourceTableLabel?: string;
  sourceSearchLabel?: string;
  sourceSearchContainer?: HTMLElement | null;
  sourceNoMatch?: ReactNode;
  targetHeading: string;
  targetTableLabel?: string;
  mappedHeading: string;
  actionsHeading?: string;
  actionsClassName?: string;
  sourceEmpty: ReactNode;
  targetEmpty: ReactNode;
  sourceScrollClassName?: string;
  targetScrollClassName?: string;
  targetTableClassName?: string;
  targetColGroup?: ReactNode;
  onSelectSource: (source: string | null) => void;
  onSetTargetEnabled?: (target: string, enabled: boolean) => void;
  onSetTargetRequired?: (target: string, required: boolean) => void;
  onDragStart?: (source: string) => void;
  onDragEnd?: () => void;
  canDrop: (source: string, target: string) => boolean;
  onDropSource: (source: string, target: string) => void;
  renderTarget: (target: SchemaMappingBlockTarget, selectedSource: string | null) => ReactNode;
  renderActions?: (target: SchemaMappingBlockTarget) => ReactNode;
  renderTargetHeading?: (target: SchemaMappingBlockTarget) => ReactNode;
}) {
  const [sourceSearch, setSourceSearch] = useState('');
  const visibleSources = sources.filter(source => `${source.id} ${source.searchText ?? ''}`.toLowerCase().includes(sourceSearch.trim().toLowerCase()));
  useEffect(() => {
    if (selectedSource && !sources.some(source => source.id === selectedSource)) onSelectSource(null);
  }, [onSelectSource, selectedSource, sources]);
  const targetColumnCount = renderActions ? 3 : 2;
  const sourceSearchControl = sourceSearchLabel && <label className="schema-mapping-block-search">{sourceSearchLabel}
        <input type="search" aria-label={sourceSearchLabel} value={sourceSearch} onChange={event => {
          const search = event.target.value;
          setSourceSearch(search);
          if (selectedSource && !sources.some(source => `${source.id} ${source.searchText ?? ''}`
            .toLowerCase().includes(search.trim().toLowerCase()))) onSelectSource(null);
        }} />
      </label>;
  return <>
    {sourceSearchControl && (sourceSearchContainer ? createPortal(sourceSearchControl, sourceSearchContainer) : sourceSearchControl)}
    <SchemaMappingPanels ariaLabel={ariaLabel}>
    <div className={['inbound-field-mapper-input-scroll', sourceScrollClassName].filter(Boolean).join(' ')}>
      <table aria-label={sourceTableLabel ?? sourceHeading}>
        <thead><tr><th scope="col">{sourceHeading}</th></tr></thead>
        <tbody>{visibleSources.map(source => <tr key={source.id}><td>
          <SchemaMappingSourceButton source={source} dragType={dragType} selected={selectedSource === source.id} disabled={disabled}
            onSelect={onSelectSource} onDragStart={onDragStart} onDragEnd={onDragEnd} />
        </td></tr>)}
        {!visibleSources.length && <tr><td>{sources.length && sourceSearch ? sourceNoMatch ?? 'No input fields match this search.' : sourceEmpty}</td></tr>}</tbody>
      </table>
    </div>
    <div className={['inbound-field-mapper-input-scroll', targetScrollClassName].filter(Boolean).join(' ')}>
      <table className={targetTableClassName || undefined} aria-label={targetTableLabel ?? targetHeading}>
        {targetColGroup}
        <thead><tr><th scope="col">{targetHeading}</th><th scope="col">{mappedHeading}</th>
          {renderActions && <th scope="col" aria-label={actionsHeading ?? 'Mapping actions'} />}</tr></thead>
        <tbody>{targets.map(target => <tr key={target.id}>
          <th scope="row" className="schema-mapping-block-output-heading">
            {target.selectable && <label title={target.required ? 'Clear Required before deselecting this output.' : undefined}><input type="checkbox" aria-label={`Select output ${target.id}`} checked={target.enabled || target.required || false}
              disabled={disabled || target.required} onChange={event => onSetTargetEnabled?.(target.id, event.target.checked)} /> <strong>{target.label}</strong></label>}
            {renderTargetHeading ? renderTargetHeading(target) : <>
            {!target.selectable && <strong>{target.label}</strong>}{target.details !== undefined && <small>{target.details}</small>}
            {target.note !== undefined && <small>{target.note}</small>}
          </>}
            {target.requiredToggle && <label className="outbound-action-required-toggle">
              <input type="checkbox" aria-label={`${target.requiredLabel ?? 'Required'} ${target.id}`} checked={target.required ?? false} disabled={disabled || target.requiredDisabled}
                onChange={event => onSetTargetRequired?.(target.id, event.target.checked)} />
              {target.requiredLabel ?? 'Required'}
            </label>}
          </th>
          <SchemaMappingDropTarget className={[
            'schema-mapping-block-target',
            target.state ? `is-${target.state}` : '',
            target.className ?? '',
          ].filter(Boolean).join(' ')} dragType={dragType} disabled={disabled}
            canDrop={source => canDrop(source, target.id)} onDropSource={source => onDropSource(source, target.id)}>
            {renderTarget(target, selectedSource)}
          </SchemaMappingDropTarget>
          {renderActions && <td className={['schema-mapping-block-actions', actionsClassName ?? ''].filter(Boolean).join(' ')}>{renderActions(target)}</td>}
        </tr>)}
        {!targets.length && <tr><td colSpan={targetColumnCount}>{targetEmpty}</td></tr>}</tbody>
      </table>
    </div>
  </SchemaMappingPanels>
  </>;
}

export function SchemaMappingDropTarget({ dragType, disabled = false, canDrop, onDropSource, className, children }: {
  dragType: string;
  disabled?: boolean;
  canDrop: (source: string) => boolean;
  onDropSource: (source: string) => void;
  className?: string;
  children: ReactNode;
}) {
  return <td className={['inbound-field-mapper-target', className].filter(Boolean).join(' ')}
    onDragOver={event => {
      if (disabled || !Array.from(event.dataTransfer.types).includes(dragType)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }} onDrop={event => {
      const source = event.dataTransfer.getData(dragType);
      if (!disabled && source && canDrop(source)) {
        event.preventDefault();
        onDropSource(source);
      }
    }}>{children}</td>;
}

export function SchemaMappingSeparatorEditor({ presetLabel, valueLabel, value, onChange }: {
  presetLabel: string;
  valueLabel: string;
  value?: string;
  onChange: (value: string) => void;
}) {
  const separator = value ?? ' ';
  const preset = separator === ' ' ? 'space' : separator === '\n' ? 'newline' : separator === ', ' ? 'comma' : 'custom';
  return <div className="inbound-field-mapper-separator">
    <label>{presetLabel}<select aria-label={presetLabel} value={preset} onChange={event => {
      const values: Record<string, string> = { space: ' ', newline: '\n', comma: ', ', custom: '' };
      onChange(values[event.target.value] ?? '');
    }}>
      <option value="space">Space</option><option value="newline">Newline</option><option value="comma">Comma</option><option value="custom">Custom</option>
    </select></label>
    <label>{valueLabel}<textarea rows={2} maxLength={256} aria-label={valueLabel} value={separator} onChange={event => onChange(event.target.value)} /></label>
  </div>;
}

export function SchemaMappingStaticButton({ label, title, disabled = false, onClick }: {
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return <button type="button" className="inbound-field-mapper-static" aria-label={label} title={title ?? label} disabled={disabled} onClick={onClick}>
    <Type size={16} aria-hidden="true" />
  </button>;
}

export function SchemaMappingRemoveButton({ label, disabled = false, onClick }: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return <button type="button" className="inbound-field-mapper-detach" aria-label={label} title={label} disabled={disabled} onClick={onClick}>
    <X size={16} aria-hidden="true" />
  </button>;
}

export function SchemaMappingTargetActions({ controls, joinLabel, staticLabel, removeLabel, joinTitle, staticTitle,
  joinBlocked = false, staticBlocked = false, onJoin, onStatic, onRemove }: {
  controls: ReturnType<typeof mappingTargetState>;
  joinLabel: string;
  staticLabel: string;
  removeLabel: string;
  joinTitle?: string;
  staticTitle?: string;
  joinBlocked?: boolean;
  staticBlocked?: boolean;
  onJoin: () => void;
  onStatic: () => void;
  onRemove: () => void;
}) {
  return <div className="outbound-action-control-group">
    {controls.joinVisible && <SchemaMappingJoinButton joined={controls.joined} label={joinLabel} title={joinTitle ?? joinLabel}
      disabled={controls.joinDisabled || joinBlocked} onClick={onJoin} />}
    <SchemaMappingStaticButton label={staticLabel} title={staticTitle} disabled={controls.staticDisabled || staticBlocked} onClick={onStatic} />
    {controls.removeVisible && <SchemaMappingRemoveButton label={removeLabel} disabled={controls.removeDisabled} onClick={onRemove} />}
  </div>;
}

export function SchemaMappingJoinedSources({ label, sources, className = '', sourceLabel, removeLabel, onRemove, onReorder }: {
  label: string;
  sources: string[];
  className?: string;
  sourceLabel?: (source: string) => string;
  removeLabel?: (source: string) => string;
  onRemove: (source: string) => void;
  onReorder?: (sources: string[]) => void;
}) {
  const displaySource = (source: string) => sourceLabel?.(source) ?? source;
  const move = (index: number, nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= sources.length) return;
    const reordered = [...sources];
    const [source] = reordered.splice(index, 1);
    reordered.splice(nextIndex, 0, source);
    onReorder?.(reordered);
  };
  return <ol className={['schema-mapping-joined-sources', className].filter(Boolean).join(' ')} aria-label={label}>
    {sources.map((source, index) => <li key={`${source}-${index}`}>
      <span>{displaySource(source)}</span>
      <div className="schema-mapping-joined-source-actions">
        {onReorder && <>
          <button type="button" aria-label={`Move ${displaySource(source)} up in ${label}`} title={`Move ${displaySource(source)} up`}
            disabled={index === 0} onClick={() => move(index, index - 1)}><ChevronUp size={16} aria-hidden="true" /></button>
          <button type="button" aria-label={`Move ${displaySource(source)} down in ${label}`} title={`Move ${displaySource(source)} down`}
            disabled={index === sources.length - 1} onClick={() => move(index, index + 1)}><ChevronDown size={16} aria-hidden="true" /></button>
        </>}
        <button type="button" aria-label={removeLabel?.(source) ?? `Remove ${displaySource(source)} from ${label}`}
          title={removeLabel?.(source) ?? `Remove ${displaySource(source)} from ${label}`} onClick={() => onRemove(source)}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </li>)}
  </ol>;
}

export function SchemaMappingJoinButton({ joined, label, title, disabled = false, onClick }: {
  joined: boolean;
  label: string;
  title: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return <button type="button" className="schema-mapping-join-toggle" aria-label={label} title={title}
    disabled={disabled} onClick={onClick}>
    {joined ? <Unlink size={16} aria-hidden="true" /> : <GitMerge size={16} aria-hidden="true" />}
  </button>;
}